import { useMemo } from 'react';
import { useI18n } from '../../lib/i18n';
import { pickName } from './helpers';
import type { KdsAllDayRow } from '../../types/kds';

// ─────────────────────────────────────────────────────────────────────────────
// All-day counts.
//
// "How many wings am I into" is the most-asked question in a busy kitchen and
// the one the legacy screen could not answer at all — the only way to know was
// for someone to walk the board counting cards. Aggregating item quantities
// across every open ticket turns that into a glance, and it is what lets a cook
// batch: twelve wings across nine tickets go in the fryer once, not nine times.
//
// Grouped by station, because a cook wants their own numbers and the grill
// knowing the bar is nine drinks behind helps nobody.
// ─────────────────────────────────────────────────────────────────────────────

export function AllDayBoard(props: { rows: KdsAllDayRow[] }): JSX.Element {
  const { lang, t } = useI18n();

  const groups = useMemo(() => {
    const byStation = new Map<string, KdsAllDayRow[]>();
    for (const row of props.rows) {
      const key = row.stationName ?? '';
      const list = byStation.get(key) ?? [];
      list.push(row);
      byStation.set(key, list);
    }
    return [...byStation.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [props.rows]);

  if (props.rows.length === 0) {
    return <div className="kds-empty">{t('nothingOnBoard')}</div>;
  }

  return (
    <div className="kds-allday">
      {groups.map(([stationName, rows]) => (
        <section key={stationName} className="kds-allday-group">
          <h2>
            <bdi>{stationName || t('unassigned')}</bdi>
          </h2>
          <table className="kds-allday-table">
            <thead>
              <tr>
                <th scope="col">{t('item')}</th>
                <th scope="col" className="num">
                  {t('waiting')}
                </th>
                <th scope="col" className="num">
                  {t('cooking')}
                </th>
                <th scope="col" className="num">
                  {t('ready')}
                </th>
                <th scope="col" className="num total">
                  {t('total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.stationId}-${row.itemId}`}>
                  {/* Deliberately NOT `.num` on this cell. That class carries
                      `width: 1%` to shrink-wrap the numeric columns; on the
                      name column it collapses the whole table — the same trap
                      the admin tables hit. */}
                  <td>
                    <bdi>{pickName(lang, row.name, row.nameAr)}</bdi>
                  </td>
                  <td className="num">{fmt(row.qtyQueued)}</td>
                  <td className="num">{fmt(row.qtyPreparing)}</td>
                  <td className="num ready">{fmt(row.qtyReady)}</td>
                  <td className="num total">{fmt(row.qtyTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

/** Zero renders as a middot: a column of "0"s reads as data, and the eye has
 *  to work to find the counts that matter. */
function fmt(qty: number): string {
  if (!qty) return '·';
  return Number.isInteger(qty) ? String(qty) : String(Number(qty.toFixed(2)));
}
